import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, Check, ChevronDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useCart } from "../context/CartContext";

const NavLink = ({ to, label, onClick, highlight }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={`nav-link-${label.toLowerCase()}`}
    className={`link-sweep text-sm uppercase tracking-[0.2em] transition-colors duration-300 ${
      highlight ? "text-champagne hover:text-champagne-light" : "text-white/70 hover:text-white"
    }`}
  >
    {label}
  </Link>
);

const LanguageSwitcher = () => {
  const { lang, langs } = useLang();
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  const switchLang = (l) => {
    const path = window.location.pathname;
    const bare = path.replace(/^\/(en|fr)(?=\/|$)/, "") || "/";
    const target = l === "de" ? bare : `/${l}${bare === "/" ? "" : bare}`;
    window.location.assign(target || "/");
  };

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="lang-switcher-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        className="flex items-center gap-1.5 text-sm uppercase tracking-[0.2em] text-champagne"
      >
        {lang}
        <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-full z-50 mt-3 min-w-[130px] border border-white/10 bg-obsidian-soft/95 backdrop-blur-xl"
          >
            {langs.map((l) => (
              <button
                key={l}
                data-testid={`lang-option-${l}`}
                onClick={() => {
                  setOpen(false);
                  switchLang(l);
                }}
                className={`flex w-full items-center justify-between px-4 py-3 text-xs uppercase tracking-[0.2em] transition-colors hover:bg-white/5 ${
                  lang === l ? "text-champagne" : "text-white/60"
                }`}
              >
                {l === "de" ? "Deutsch" : l === "en" ? "English" : "Français"}
                {lang === l && <Check size={13} />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Navbar = () => {
  const { t } = useLang();
  const { count } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const links = [
    { to: "/about", label: t.nav.about },
    { to: "/interior", label: t.nav.interior },
    { to: "/fashion", label: t.nav.fashion },
    { to: "/wine", label: t.nav.wine },
    { to: "/real-estate", label: t.nav.realEstate },
    { to: "/consulting", label: t.nav.consulting, highlight: true },
    { to: "/library", label: t.nav.library },
    { to: "/live-products", label: t.nav.products },
    { to: "/shop", label: t.nav.shop },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color,padding] duration-500 ${
          scrolled || mobileOpen
            ? "border-b border-white/10 bg-obsidian/80 py-4 backdrop-blur-2xl"
            : "border-b border-transparent py-6"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 md:px-12">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <img src="/mysymp-logo-gold.png" alt="MYSYMP AG" className="h-7 w-auto md:h-8" />
            <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-champagne">AG</span>
          </Link>

          <nav className="hidden items-center gap-7 xl:flex">
            {links.map((l) => (
              <NavLink key={l.to} {...l} />
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <LanguageSwitcher />
            <Link to="/shop" data-testid="nav-cart" className="relative text-white/80 hover:text-champagne transition-colors">
              <ShoppingBag size={18} strokeWidth={1.4} />
              {count > 0 && (
                <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center bg-champagne text-[9px] font-medium text-obsidian">
                  {count}
                </span>
              )}
            </Link>
            <Link
              to="/contact"
              data-testid="nav-book-cta"
              className="hidden border border-champagne/50 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-champagne transition-colors duration-300 hover:bg-champagne hover:text-obsidian md:inline-block"
            >
              {t.nav.book}
            </Link>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileOpen((o) => !o)}
              className="text-white xl:hidden"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-30 flex flex-col justify-center bg-obsidian px-8 xl:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-5">
              {[{ to: "/", label: t.nav.home }, ...links, { to: "/contact", label: t.nav.contact }].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.05 }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    className={`font-serif text-4xl transition-colors ${l.highlight ? "text-champagne hover:text-champagne-light" : "text-white/90 hover:text-champagne"}`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
